import { TERMS, getBattery } from "@/data/tokens";
import { compositeReportStatus, getStudentAssessment } from "@/lib/report-data";
import { resolveStudentOutcomes } from "@/lib/task-family-bands";
import { riskLabel, statusLabel, taskBandLabel } from "@/lib/copy";
import type { ClassRoom, School, Student, TermId } from "@/types";

function csvEscape(value: string | number | null | undefined): string {
  if (value == null) return "";
  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

export function downloadCsv(filename: string, rows: (string | number | null | undefined)[][]): void {
  const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function yearLevelLabel(year: ClassRoom["year"]): string {
  return year === "F" ? "Foundation" : "Year 1";
}

function mergeCsvRows(rowGroups: (string | number | null | undefined)[][][]): (string | number | null | undefined)[][] {
  const header = Array.from(new Set(rowGroups.flatMap((rows) => rows[0] ?? [])));
  const merged = [header];
  for (const rows of rowGroups) {
    const sourceHeader = rows[0] ?? [];
    for (const row of rows.slice(1)) {
      const byColumn = new Map(sourceHeader.map((column, index) => [column, row[index]]));
      merged.push(header.map((column) => byColumn.get(column)));
    }
  }
  return merged;
}

export function classStudentCsvRows(klass: ClassRoom, students: Student[], term: TermId | string): (string | number | null | undefined)[][] {
  const screeningPeriod = TERMS.find((candidate) => candidate.id === term)?.label ?? String(term);
  const battery = getBattery(klass.year, klass.cohort || "A", term);
  const taskColumns = battery.flatMap((task) => [`${task.id}_pctl`, `${task.id}_achievement`]);
  const rows: (string | number | null | undefined)[][] = [[
    "year_level",
    "class",
    "student_id",
    "student_name",
    "screening_period",
    "completion_status",
    "enssa_composite_score",
    "overall_percentile_rank",
    "overall_achievement",
    ...taskColumns,
  ]];
  for (const student of students) {
    const assessment = getStudentAssessment(student, term);
    const outcomes = new Map(resolveStudentOutcomes(student, battery, term).map(({ task, outcome }) => [task.id, outcome]));
    const taskValues = battery.flatMap((task) => {
      const outcome = outcomes.get(task.id);
      if (!outcome) return [null, ""];
      const reportPercentile = outcome.band === "limited" || outcome.band === "not_attempted" ? null : outcome.percentile;
      return [reportPercentile == null ? null : Math.round(reportPercentile), taskBandLabel(outcome.band)];
    });
    const reportStatus = compositeReportStatus(assessment);
    const reportOverall = reportStatus !== "suppressed";
    rows.push([
      yearLevelLabel(klass.year),
      klass.name || klass.id,
      student.id,
      student.name,
      screeningPeriod,
      statusLabel(assessment.status),
      reportOverall && assessment.compositeScore != null ? Math.round(assessment.compositeScore) : null,
      reportOverall && assessment.overallPercentile != null ? Math.round(assessment.overallPercentile) : null,
      reportOverall ? riskLabel(assessment.risk) : "No classification",
      ...taskValues,
    ]);
  }
  return rows;
}

export function schoolStudentCsvRows(school: School, term: TermId | string): (string | number | null | undefined)[][] {
  return mergeCsvRows(school.classes.map((klass) => classStudentCsvRows(klass, klass.students, term)));
}
